const EXPANDED_ATTR = 'data-promo-expanded';
const CODE_TOKEN = '%code%';
const KEYBOARD_FALLBACK_DELAY = 300;

class CartPromo extends HTMLElement {
  constructor() {
    super();
    this._draftValue = '';
    this._isExpanded = false;
    this._keyboardController = null;
    this._hadFocus = false;
  }

  connectedCallback() {
    // Delegated on the root, which survives every morph — the children do not.
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-promo-apply-input]')) {
        this.applyCode(this._input()?.value, event.target.closest('[data-promo-apply-input]'));
        return;
      }

      const codeButton = event.target.closest('[data-promo-apply-code]');
      if (codeButton) {
        this.applyCode(codeButton.dataset.code, codeButton);
        return;
      }

      if (event.target.closest('[data-promo-toggle]')) this.toggleExpanded();
    });

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' || !event.target.closest('[data-cart-promo-input]')) return;
      event.preventDefault();
      this.applyCode(event.target.value, this.querySelector('[data-promo-apply-input]'));
    });

    this.addEventListener('input', (event) => {
      if (!event.target.closest('[data-cart-promo-input]')) return;
      this._draftValue = event.target.value;
    });

    // `focusin` bubbles and `focus` does not — a delegated `focus` listener would never fire.
    this.handleFocusIn = (event) => this._handleFocusIn(event);
    this.addEventListener('focusin', this.handleFocusIn);

    // `cart:mutation-end` is global, so focus is only reclaimed when this region
    // owned it before the morph — otherwise a line-quantity bump would yank it here.
    this.handleMutationStart = () => {
      this._hadFocus = this.contains(document.activeElement);
    };
    document.addEventListener('cart:mutation-start', this.handleMutationStart);

    this.handleMutationEnd = () => this._handleMutationEnd();
    document.addEventListener('cart:mutation-end', this.handleMutationEnd);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:mutation-start', this.handleMutationStart);
    document.removeEventListener('cart:mutation-end', this.handleMutationEnd);
    this.removeEventListener('focusin', this.handleFocusIn);
    this._keyboardController?.abort();
    this._keyboardController = null;
  }

  // Below md the browser scrolls the focused input into view before the keyboard resizes the
  // visual viewport, so the scroll is undone by it — redo it once the viewport settles.
  _handleFocusIn(event) {
    const input = event.target.closest('[data-cart-promo-input]');
    if (!input || !window.matchMedia('(max-width: 767px)').matches) return;

    this._keyboardController?.abort();
    this._keyboardController = new AbortController();
    const { signal } = this._keyboardController;
    const scrollIntoView = () => input.scrollIntoView({ block: 'center' });

    if (!window.visualViewport) {
      const timer = setTimeout(scrollIntoView, KEYBOARD_FALLBACK_DELAY);
      signal.addEventListener('abort', () => clearTimeout(timer));
      return;
    }

    window.visualViewport.addEventListener('resize', scrollIntoView, { once: true, signal });
  }

  async applyCode(code, control = null) {
    const nextCode = (code ?? '').trim();
    if (!nextCode) return;

    const drawer = this.closest('cart-drawer');
    if (!drawer) return;

    const prior = await this._appliedCodes();
    if (prior === null) {
      CartMutations.showError(drawer, { control, scope: 'promo', message: null });
      return;
    }

    if (prior.some((priorCode) => priorCode.toLowerCase() === nextCode.toLowerCase())) {
      const template = this.dataset.duplicateMessage ?? '';
      CartMutations.showError(drawer, {
        control,
        scope: 'promo',
        message: template.replace(CODE_TOKEN, nextCode),
      });
      this._clearInput();
      return;
    }

    const result = await drawer.mutate(
      (sections) => CartAPI.update({ discount: [...prior, nextCode].join(',') }, sections),
      {
        control,
        // A function, not a string: /cart/update.js returns 200 for a rejected
        // code, so acceptance is only knowable from the response.
        announce: (data) =>
          this._isAccepted(data, nextCode)
            ? (drawer.dataset.announceCodeApplied ?? '').replace(CODE_TOKEN, nextCode)
            : null,
      },
    );

    // A transport failure already painted the error.
    if (!result.ok) return;

    if (this._isAccepted(result.data, nextCode)) {
      this._draftValue = '';
      this._clearInput();
      return;
    }

    // Restore first — a morph after the error would wipe it. The draft stays so the
    // shopper can correct a typo; an empty join clears the stored dead code.
    await drawer.mutate((sections) => CartAPI.update({ discount: prior.join(',') }, sections), { control });

    // Named scope, not `control`: the restore mutation above ran a full morph, which
    // may have detached this button — and a detached control resolves to the global
    // slot at the top of the panel, far from the input the message is about.
    CartMutations.showError(drawer, { control, scope: 'promo', message: null });
  }

  // `applicable` is the only signal that survives: a free-shipping code is
  // applicable without producing a discount row.
  _isAccepted(data, code) {
    return (data?.discount_codes ?? []).some(
      (discount) => discount.code.toLowerCase() === code.toLowerCase() && discount.applicable,
    );
  }

  toggleExpanded() {
    const track = this.querySelector('carousel-track');

    if (this._isExpanded) {
      this._isExpanded = false;
      this._applyExpandedState();
      // After the flip, so Embla measures the restored horizontal row.
      track?.reInit();
      return;
    }

    track?.destroyCarousel();
    this._isExpanded = true;
    this._applyExpandedState();
  }

  _applyExpandedState() {
    this.setAttribute(EXPANDED_ATTR, String(this._isExpanded));

    const toggle = this.querySelector('[data-promo-toggle]');
    if (!toggle) return;

    toggle.setAttribute('aria-expanded', String(this._isExpanded));
    const label = toggle.querySelector('[data-promo-toggle-label]');
    if (!label) return;

    const { seeAllLabel = '', seeLessLabel = '' } = toggle.dataset;
    label.textContent = this._isExpanded ? seeLessLabel : seeAllLabel;
  }

  // The morph repaints this region collapsed and empty, so both are restored here.
  _handleMutationEnd() {
    this._applyExpandedState();

    const input = this._input();
    if (!input || !this._draftValue || input.value) return;

    input.value = this._draftValue;
    if (!this._hadFocus) return;

    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
  }

  // `/cart/update.js` replaces the entire discount list, so the prior codes must be
  // known before sending. An uninitialised store reads as "no codes", which would
  // silently wipe them — so init is awaited, and null is returned if it still fails.
  async _appliedCodes() {
    if (!window.CartStore?.state) await window.CartStore?.init();

    const { discount_codes: discountCodes } = window.CartStore?.state ?? {};
    if (!discountCodes) return null;

    return discountCodes.filter((discount) => discount.applicable).map((discount) => discount.code);
  }

  _input() {
    return this.querySelector('[data-cart-promo-input]');
  }

  _clearInput() {
    const input = this._input();
    if (input) input.value = '';
  }
}

if (!customElements.get('cart-promo')) {
  customElements.define('cart-promo', CartPromo);
}
